import type { Include } from '@dynatrace-sdk/client-filter-segment-management';

import type {
  FilterFieldNode,
  FilterFieldTree,
} from '../filter-field/types/tree-nodes.js';
import {
  isFilterFieldGroupNode,
  isFilterFieldStatementNode,
} from '../filter-field/utils/tree-node-guards.js';
import {
  FIELDS_SNAPSHOT_COVERED,
  SIGNAL_DATA_VALUES,
} from './applicability.types.js';
import type {
  ApplicabilityVerdict,
  EntityDataObject,
  RequiredDataObject,
  SegmentCoverage,
  SignalDataObject,
} from './applicability.types.js';
import { parsePossibleJson } from './utils.js';

const ALL_DATA_OBJECT = '_all_data_object';
const ALL_ENTITIES = '_all_entities';

/** Hardcoded, not tenant input - skips `FIELDS_SNAPSHOT_COVERED` validation. */
export const ENTITY_FIELDS_SNAPSHOT_SOURCE = 'smartscape.nodes';

/** Fields on the required entity type(s) - non-empty also proves the type exists, shared by `checkEntityTypeSupport` and `checkFilterFields`. */
export const ENTITY_TYPE_FIELDS_SOURCE = 'entity-type-fields';

function isAllDataObjectInclude(dataObject: string): boolean {
  return dataObject === ALL_DATA_OBJECT;
}

function isAllEntitiesInclude(dataObject: string): boolean {
  return dataObject === ALL_ENTITIES;
}

function isSignalDataObjectValue(
  dataObject: string,
): dataObject is SignalDataObject['dataObject'] {
  return (SIGNAL_DATA_VALUES as readonly string[]).includes(dataObject);
}

function isEntityRequirement(
  requirement: RequiredDataObject,
): requirement is EntityDataObject {
  return requirement.dataObject === 'entities';
}

/**
 * Derives what a segment covers. `_all_data_object` saturates everything;
 * `_all_entities` covers entities only. Classic `dt.entity.<type>` /
 * `_classic_entities` includes are intentionally ignored.
 * @internal
 */
export function getSegmentCoverage(includes: Include[] = []): SegmentCoverage {
  const coverage: SegmentCoverage = {
    signalDataTypes: new Set(),
    hasGrailEntityCoverage: false,
  };

  for (const { dataObject } of includes) {
    if (isAllDataObjectInclude(dataObject)) {
      // Fully saturates every dimension; nothing later can change the result.
      coverage.hasGrailEntityCoverage = true;
      coverage.signalDataTypes = 'all';
      break;
    } else if (isAllEntitiesInclude(dataObject)) {
      coverage.hasGrailEntityCoverage = true;
    } else if (isSignalDataObjectValue(dataObject)) {
      if (coverage.signalDataTypes !== 'all') {
        coverage.signalDataTypes.add(dataObject);
      }
    }
  }

  return coverage;
}

/**
 * Data objects needing a `fieldsSnapshot` lookup, limited to the sources the
 * view reads - `checkFilterFields` skips the rest, so querying them is waste.
 * Signal values are checked against `FIELDS_SNAPSHOT_COVERED` -
 * tenant-authored, must never reach the query builder unvalidated.
 * @internal
 */
export function getFieldsSnapshotCandidates(
  includes: Include[] = [],
  requires: RequiredDataObject[] = [],
): string[] {
  const filtered = includes.filter((include) => Boolean(include.filter));
  const requiredDataTypes = new Set<string>(
    requires.map((requirement) => requirement.dataObject),
  );
  const hasEntityRequirement = requires.some(isEntityRequirement);

  const fromIncludes = filtered.map((include) =>
    includeSnapshotSource(
      include.dataObject,
      requiredDataTypes,
      hasEntityRequirement,
    ),
  );

  // An `_all_data_object` filter is cross-checked against everything the view
  // reads, so the requirements themselves become candidates.
  const fromRequirements = filtered.some((include) =>
    isAllDataObjectInclude(include.dataObject),
  )
    ? requirementSnapshotSources(requires)
    : [];

  const candidates = new Set(
    [...fromIncludes, ...fromRequirements].filter(
      (source): source is string => source !== null,
    ),
  );

  return [...candidates].sort((a, b) => a.localeCompare(b));
}

/** `null` when the view never reads this include, so its fields go unchecked. */
function includeSnapshotSource(
  dataObject: string,
  requiredDataTypes: ReadonlySet<string>,
  hasEntityRequirement: boolean,
): string | null {
  if (isAllEntitiesInclude(dataObject)) {
    return hasEntityRequirement ? ENTITY_FIELDS_SNAPSHOT_SOURCE : null;
  }

  return requiredDataTypes.has(dataObject) &&
    FIELDS_SNAPSHOT_COVERED.has(dataObject)
    ? dataObject
    : null;
}

function requirementSnapshotSources(requires: RequiredDataObject[]): string[] {
  return requires
    .map((requirement) =>
      isEntityRequirement(requirement)
        ? ENTITY_FIELDS_SNAPSHOT_SOURCE
        : requirement.dataObject,
    )
    .filter(
      (source) =>
        source === ENTITY_FIELDS_SNAPSHOT_SOURCE ||
        FIELDS_SNAPSHOT_COVERED.has(source),
    );
}

/**
 * First-mismatch-wins verdict: checks signals, entities, entity types, then
 * filter fields in order, stopping at the first zero-overlap dimension.
 * Partial coverage never warns.
 * @internal
 */
export function getApplicabilityVerdict(
  coverage: SegmentCoverage,
  requires: RequiredDataObject[] = [],
  includes: Include[] = [],
  fieldsByDataObject: ReadonlyMap<string, ReadonlySet<string>> = new Map(),
): ApplicabilityVerdict {
  const signalRequirements = requires.filter(
    (requirement): requirement is SignalDataObject =>
      !isEntityRequirement(requirement),
  );
  const entityRequirements = requires.filter(isEntityRequirement);
  const requiredEntityTypePatterns = getRequiredEntityTypePatterns(
    coverage,
    requires,
  );

  return (
    checkSignals(coverage, signalRequirements) ??
    checkEntities(coverage, entityRequirements) ??
    checkEntityTypeSupport(coverage, entityRequirements, fieldsByDataObject) ??
    checkFilterFields(
      includes,
      signalRequirements,
      entityRequirements,
      requiredEntityTypePatterns,
      fieldsByDataObject,
    ) ?? {
      applicable: true,
    }
  );
}

function toDistinctDataTypes(
  signalRequirements: SignalDataObject[],
): Array<SignalDataObject['dataObject']> {
  return [...new Set(signalRequirements.map((r) => r.dataObject))];
}

/** Any overlap between required and covered signal types passes - partial coverage is fine. */
function checkSignals(
  coverage: SegmentCoverage,
  signalRequirements: SignalDataObject[],
): ApplicabilityVerdict | null {
  if (signalRequirements.length === 0) {
    return null;
  }

  const requiredDataTypes = toDistinctDataTypes(signalRequirements);

  const { signalDataTypes } = coverage;
  const isCovered =
    signalDataTypes === 'all' ||
    requiredDataTypes.some((dataType) => signalDataTypes.has(dataType));

  return isCovered
    ? null
    : {
        applicable: false,
        reason: 'signals-mismatch',
        missingDataTypes: requiredDataTypes,
      };
}

/** Only unconditional coverage satisfies this (no per-type Grail source exists) - types aren't reported since the fix is the same either way. */
function checkEntities(
  coverage: SegmentCoverage,
  entityRequirements: EntityDataObject[],
): ApplicabilityVerdict | null {
  if (entityRequirements.length === 0 || coverage.hasGrailEntityCoverage) {
    return null;
  }

  return {
    applicable: false,
    reason: 'entities-missing',
  };
}

/**
 * Entity type patterns to validate via `checkEntityTypeSupport` - empty
 * unless the segment already has unconditional Grail coverage.
 * @internal
 */
export function getRequiredEntityTypePatterns(
  coverage: SegmentCoverage,
  requires: RequiredDataObject[] = [],
): string[] {
  if (!coverage.hasGrailEntityCoverage) {
    return [];
  }

  const patterns = requires
    .filter(isEntityRequirement)
    .flatMap((r) => r.entityTypes ?? []);

  return [...new Set(patterns)].sort((a, b) => a.localeCompare(b));
}

/**
 * Unconditional coverage still fails if the tenant has no data for any
 * required entity type (see `ENTITY_TYPE_FIELDS_SOURCE`); partial coverage
 * passes.
 */
function checkEntityTypeSupport(
  coverage: SegmentCoverage,
  entityRequirements: EntityDataObject[],
  fieldsByDataObject: ReadonlyMap<string, ReadonlySet<string>>,
): ApplicabilityVerdict | null {
  if (entityRequirements.length === 0 || !coverage.hasGrailEntityCoverage) {
    return null;
  }

  const requiredEntityTypePatterns = getRequiredEntityTypePatterns(
    coverage,
    entityRequirements,
  );

  if (requiredEntityTypePatterns.length === 0) {
    return null;
  }

  const supportedTypes = fieldsByDataObject.get(ENTITY_TYPE_FIELDS_SOURCE);

  if (!supportedTypes) {
    return null;
  }

  // Query already OR-filters by pattern - non-empty means some pattern
  // matched, all-or-nothing, no per-pattern attribution.
  return supportedTypes.size === 0
    ? {
        applicable: false,
        reason: 'entity-type-mismatch',
        missingEntityTypes: requiredEntityTypePatterns,
      }
    : null;
}

function collectFieldNames(
  node: FilterFieldTree | FilterFieldNode | undefined | null,
  names: Set<string>,
): void {
  if (!node) {
    return;
  }

  if (isFilterFieldGroupNode(node) && Array.isArray(node.children)) {
    for (const child of node.children) {
      collectFieldNames(child, names);
    }
    return;
  }

  if (isFilterFieldStatementNode(node) && node.key) {
    names.add(node.key.value);
  }
}

/**
 * Extracts field names from a segment include's `filter` (a serialized
 * `FilterFieldTree`) by walking every `Statement` node's `key`. An
 * unparseable filter yields no fields - false negative, never false positive.
 * @internal
 */
export function extractFieldsFromFilter(
  filter: string | undefined,
): Set<string> {
  const names = new Set<string>();

  if (!filter) {
    return names;
  }

  const tree: unknown = parsePossibleJson(filter);

  if (typeof tree !== 'object' || tree === null) {
    return names;
  }

  collectFieldNames(tree as FilterFieldTree, names);

  return names;
}

/**
 * Include-self-validation: checks a specific-signal include's fields against
 * its own `fieldsSnapshot` entry. Skipped when the data object isn't
 * covered, or its snapshot hasn't been fetched.
 */
function findMissingFieldsForInclude(
  dataObject: string,
  referencedFields: Set<string>,
  fieldsByDataObject: ReadonlyMap<string, ReadonlySet<string>>,
): string[] {
  if (
    dataObject !== ENTITY_FIELDS_SNAPSHOT_SOURCE &&
    dataObject !== ENTITY_TYPE_FIELDS_SOURCE &&
    !FIELDS_SNAPSHOT_COVERED.has(dataObject)
  ) {
    return [];
  }

  if (!fieldsByDataObject.has(dataObject)) {
    return [];
  }

  return lacksEveryFieldIn(referencedFields, fieldsByDataObject)(dataObject)
    ? [...referencedFields]
    : [];
}

interface AllDataFieldCheckResult {
  missingFields: string[];
  missingFieldsSignals: string[];
  hasEntityFieldsGap: boolean;
}

/**
 * View-cross-check: an `_all_data_object` include's fields are checked
 * against signal sources and entity source independently so entity gaps are
 * not masked by signal coverage, and vice versa.
 */
function findMissingFieldsForAllDataInclude(
  referencedFields: Set<string>,
  {
    requiredDataTypes,
    hasEntityRequirement,
    entityFieldsSource,
    fieldsByDataObject,
  }: FieldCheckContext,
): AllDataFieldCheckResult {
  const signalSources = requiredDataTypes.filter(
    (dataType) =>
      FIELDS_SNAPSHOT_COVERED.has(dataType) && fieldsByDataObject.has(dataType),
  );

  const lacksEveryField = lacksEveryFieldIn(
    referencedFields,
    fieldsByDataObject,
  );

  const hasSignalGap =
    signalSources.length > 0 && signalSources.every(lacksEveryField);
  const hasEntityFieldsGap =
    hasEntityRequirement &&
    fieldsByDataObject.has(entityFieldsSource) &&
    lacksEveryField(entityFieldsSource);

  return {
    missingFields:
      hasSignalGap || hasEntityFieldsGap ? [...referencedFields] : [],
    // Named per source: one lacking every field is still worth naming when
    // the entity side is what raised the warning.
    missingFieldsSignals: signalSources.filter(lacksEveryField),
    hasEntityFieldsGap,
  };
}

/**
 * Partial coverage passes, so a source is only a gap when it is missing
 * every field the filter references.
 */
function lacksEveryFieldIn(
  referencedFields: Set<string>,
  fieldsByDataObject: ReadonlyMap<string, ReadonlySet<string>>,
) {
  return (source: string) =>
    [...referencedFields].every(
      (field) => !(fieldsByDataObject.get(source)?.has(field) ?? false),
    );
}

/**
 * The filter-field dimension: validates filter-referenced fields against
 * the `fieldsSnapshot` index (see `findMissingFieldsForInclude` and
 * `findMissingFieldsForAllDataInclude`). Entities are checked against the
 * required type(s) when declared - a field valid on one type isn't
 * necessarily valid on another. Includes are additive: one feeding include
 * without a gap clears the dimension.
 */
function checkFilterFields(
  includes: Include[],
  signalRequirements: SignalDataObject[],
  entityRequirements: EntityDataObject[],
  requiredEntityTypePatterns: string[],
  fieldsByDataObject: ReadonlyMap<string, ReadonlySet<string>>,
): ApplicabilityVerdict | null {
  const hasRequiredEntityTypes = requiredEntityTypePatterns.length > 0;

  const context: FieldCheckContext = {
    requiredDataTypes: toDistinctDataTypes(signalRequirements),
    hasEntityRequirement: entityRequirements.length > 0,
    entityFieldsSource: hasRequiredEntityTypes
      ? ENTITY_TYPE_FIELDS_SOURCE
      : ENTITY_FIELDS_SNAPSHOT_SOURCE,
    entitySourceLabels: hasRequiredEntityTypes
      ? requiredEntityTypePatterns
      : ['entities'],
    fieldsByDataObject,
  };

  const gaps: ApplicabilityVerdict[] = [];

  for (const include of includes) {
    if (!feedsRequiredSources(include, context)) {
      continue;
    }

    const verdict = checkInclude(include, context);

    if (verdict === null) {
      return null;
    }

    gaps.push(verdict);
  }

  return gaps.length > 0 ? mergeFieldMismatches(gaps) : null;
}

/** Only an include reaching data the view reads can explain an empty result. */
function feedsRequiredSources(
  include: Include,
  { requiredDataTypes, hasEntityRequirement }: FieldCheckContext,
): boolean {
  if (isAllDataObjectInclude(include.dataObject)) {
    return true;
  }

  if (isAllEntitiesInclude(include.dataObject)) {
    return hasEntityRequirement;
  }

  return requiredDataTypes.includes(include.dataObject);
}

/** Every contributing include failed, so the warning names all of them. */
function mergeFieldMismatches(
  gaps: ApplicabilityVerdict[],
): ApplicabilityVerdict {
  return {
    applicable: false,
    reason: 'field-mismatch',
    missingFields: [...new Set(gaps.flatMap((gap) => gap.missingFields ?? []))],
    missingFieldsSources: [
      ...new Set(gaps.flatMap((gap) => gap.missingFieldsSources ?? [])),
    ],
  };
}

interface FieldCheckContext {
  requiredDataTypes: string[];
  hasEntityRequirement: boolean;
  /** `fieldsSnapshot` key the entity fields were collected under. */
  entityFieldsSource: string;
  /** How an entity gap is named in the warning. */
  entitySourceLabels: string[];
  fieldsByDataObject: ReadonlyMap<string, ReadonlySet<string>>;
}

function checkInclude(
  include: Include,
  context: FieldCheckContext,
): ApplicabilityVerdict | null {
  if (!include.filter) {
    return null;
  }

  const referencedFields = extractFieldsFromFilter(include.filter);

  if (referencedFields.size === 0) {
    return null;
  }

  let missingFields: string[];
  let missingFieldsSources: string[];

  if (isAllDataObjectInclude(include.dataObject)) {
    const result = findMissingFieldsForAllDataInclude(
      referencedFields,
      context,
    );

    missingFields = result.missingFields;
    missingFieldsSources = [
      ...result.missingFieldsSignals,
      ...(result.hasEntityFieldsGap ? context.entitySourceLabels : []),
    ];
  } else if (isAllEntitiesInclude(include.dataObject)) {
    missingFields = findMissingFieldsForInclude(
      context.entityFieldsSource,
      referencedFields,
      context.fieldsByDataObject,
    );
    missingFieldsSources = context.entitySourceLabels;
  } else {
    missingFields = findMissingFieldsForInclude(
      include.dataObject,
      referencedFields,
      context.fieldsByDataObject,
    );
    missingFieldsSources = [include.dataObject];
  }

  if (missingFields.length === 0) {
    return null;
  }

  return {
    applicable: false,
    reason: 'field-mismatch',
    missingFields,
    missingFieldsSources,
  };
}
